import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Activity, Users, AlertTriangle, TrendingUp, TrendingDown,
  RefreshCw, Loader2, UserCheck, Scale, BarChart3, AlertCircle,
  ChevronRight, Heart, Zap
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const getAcuityColor = (level) => {
  const colors = {
    1: 'bg-emerald-100 text-emerald-700 border-emerald-200',
    2: 'bg-blue-100 text-blue-700 border-blue-200',
    3: 'bg-amber-100 text-amber-700 border-amber-200',
    4: 'bg-orange-100 text-orange-700 border-orange-200',
    5: 'bg-red-100 text-red-700 border-red-200'
  };
  return colors[level] || 'bg-gray-100 text-gray-700';
};

const getWorkloadColor = (status) => {
  const colors = {
    'optimal': 'text-emerald-600',
    'capacity_available': 'text-blue-600',
    'high': 'text-amber-600',
    'overloaded': 'text-red-600'
  };
  return colors[status] || 'text-gray-600';
};

export default function PatientAcuity() {
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [overview, setOverview] = useState(null);
  const [nurseData, setNurseData] = useState(null);
  const [trends, setTrends] = useState(null);
  const [highAcuity, setHighAcuity] = useState(null);
  const [workloadAnalysis, setWorkloadAnalysis] = useState(null);

  const [error, setError] = useState(null);

  const fetchData = async (retryCount = 0) => {
    setLoading(true);
    setError(null);
    const token = localStorage.getItem('qapi-token');
    
    if (!token) {
      // Wait for token to be available
      if (retryCount < 3) {
        setTimeout(() => fetchData(retryCount + 1), 1000);
        return;
      }
      setError('Please log in to view this page');
      setLoading(false);
      return;
    }
    
    const headers = { 'Authorization': `Bearer ${token}` };

    try {
      const [overviewRes, nurseRes, trendsRes, highAcuityRes, workloadRes] = await Promise.all([
        fetch(`${API_URL}/api/patient-acuity/overview`, { headers }),
        fetch(`${API_URL}/api/patient-acuity/by-nurse`, { headers }),
        fetch(`${API_URL}/api/patient-acuity/trends?weeks=8`, { headers }),
        fetch(`${API_URL}/api/patient-acuity/high-acuity`, { headers }),
        fetch(`${API_URL}/api/patient-acuity/workload-analysis`, { headers })
      ]);

      // Check for auth errors
      if (overviewRes.status === 401) {
        if (retryCount < 2) {
          setTimeout(() => fetchData(retryCount + 1), 1000);
          return;
        }
        setError('Session expired. Please refresh the page.');
        setLoading(false);
        return;
      }

      if (overviewRes.ok) setOverview(await overviewRes.json());
      if (nurseRes.ok) setNurseData(await nurseRes.json());
      if (trendsRes.ok) setTrends(await trendsRes.json());
      if (highAcuityRes.ok) setHighAcuity(await highAcuityRes.json());
      if (workloadRes.ok) setWorkloadAnalysis(await workloadRes.json());
    } catch (error) {
      console.error('Error fetching acuity data:', error);
      setError('Failed to load data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Small delay to ensure token is available after navigation
    const timer = setTimeout(() => fetchData(), 100);
    return () => clearTimeout(timer);
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
        <span className="ml-2">Loading acuity data...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6" data-testid="patient-acuity-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Patient Acuity Scoring</h1>
          <p className="text-muted-foreground">AI-powered patient acuity analysis with weighted caseloads</p>
        </div>
        <Button onClick={fetchData} variant="outline" size="sm">
          <RefreshCw className="h-4 w-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card data-testid="total-patients-card">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Patients</p>
                <p className="text-3xl font-bold">{overview?.total_patients || 0}</p>
              </div>
              <Users className="h-8 w-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>

        <Card data-testid="avg-acuity-card">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Avg Acuity</p>
                <p className="text-3xl font-bold">{overview?.statistics?.average_acuity || 0}</p>
              </div>
              <Activity className="h-8 w-8 text-amber-500" />
            </div>
            <p className="text-xs text-muted-foreground mt-1">Scale: 1-5 (5 = Critical)</p>
          </CardContent>
        </Card>

        <Card data-testid="high-acuity-card">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">High Acuity (4-5)</p>
                <p className="text-3xl font-bold text-orange-600">{overview?.statistics?.high_acuity_count || 0}</p>
              </div>
              <AlertTriangle className="h-8 w-8 text-orange-500" />
            </div>
            <p className="text-xs text-muted-foreground mt-1">{overview?.statistics?.high_acuity_percentage || 0}% of census</p>
          </CardContent>
        </Card>

        <Card data-testid="weighted-caseload-card">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Weighted Load</p>
                <p className="text-3xl font-bold">{overview?.statistics?.total_weighted_caseload || 0}</p>
              </div>
              <Scale className="h-8 w-8 text-violet-500" />
            </div>
            <p className="text-xs text-muted-foreground mt-1">Acuity-adjusted patients</p>
          </CardContent>
        </Card>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="overview">Distribution</TabsTrigger>
          <TabsTrigger value="by-nurse">By Nurse</TabsTrigger>
          <TabsTrigger value="high-acuity">High Acuity</TabsTrigger>
          <TabsTrigger value="workload">Workload Analysis</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="mt-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Acuity Distribution */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <BarChart3 className="h-5 w-5" />
                  Acuity Distribution
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {[
                    { level: 5, label: 'Critical', count: overview?.distribution?.level_5_critical || 0 },
                    { level: 4, label: 'High', count: overview?.distribution?.level_4_high || 0 },
                    { level: 3, label: 'Moderate', count: overview?.distribution?.level_3_moderate || 0 },
                    { level: 2, label: 'Mild', count: overview?.distribution?.level_2_mild || 0 },
                    { level: 1, label: 'Low', count: overview?.distribution?.level_1_low || 0 }
                  ].map(item => (
                    <div key={item.level} className="flex items-center gap-4">
                      <Badge className={`w-24 justify-center ${getAcuityColor(item.level)}`}>
                        Level {item.level}
                      </Badge>
                      <div className="flex-1">
                        <div className="flex justify-between mb-1">
                          <span className="text-sm">{item.label}</span>
                          <span className="text-sm font-medium">{item.count} patients</span>
                        </div>
                        <Progress 
                          value={(item.count / (overview?.total_patients || 1)) * 100} 
                          className="h-2"
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Trends */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  {trends?.trend_direction === 'increasing' ? 
                    <TrendingUp className="h-5 w-5 text-amber-500" /> : 
                    <TrendingDown className="h-5 w-5 text-emerald-500" />
                  }
                  Acuity Trends
                </CardTitle>
                <CardDescription>8-week trend analysis</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {trends?.trends?.slice(-6).map((week, idx) => (
                    <div key={idx} className="flex items-center justify-between p-2 rounded-lg bg-muted/30">
                      <span className="text-sm">Week {week.week}</span>
                      <div className="flex items-center gap-4">
                        <span className="text-sm">Avg: <strong>{week.average_acuity}</strong></span>
                        <Badge variant="outline" className="text-xs">
                          {week.high_acuity_percentage}% high
                        </Badge>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="mt-4 p-3 bg-muted/50 rounded-lg">
                  <p className="text-sm">
                    <strong>Trend:</strong> {trends?.trend_direction === 'increasing' ? 'Increasing' : 'Stable'}
                    <span className="text-muted-foreground ml-2">
                      ({trends?.change_percentage > 0 ? '+' : ''}{trends?.change_percentage}% change)
                    </span>
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="by-nurse" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle>Acuity Distribution by Nurse</CardTitle>
              <CardDescription>Weighted caseloads and workload status per nurse</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {nurseData?.nurses?.slice(0, 15).map((nurse, idx) => (
                  <div key={idx} className="p-4 border rounded-lg">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <UserCheck className="h-5 w-5 text-muted-foreground" />
                        <span className="font-medium">{nurse.nurse_name}</span>
                      </div>
                      <Badge className={getWorkloadColor(nurse.workload_status)}>
                        {nurse.workload_status?.replace('_', ' ')}
                      </Badge>
                    </div>
                    <div className="grid grid-cols-4 gap-4 text-sm">
                      <div>
                        <p className="text-muted-foreground">Patients</p>
                        <p className="font-semibold">{nurse.patient_count}</p>
                      </div>
                      <div>
                        <p className="text-muted-foreground">Weighted Load</p>
                        <p className="font-semibold">{nurse.weighted_caseload}</p>
                      </div>
                      <div>
                        <p className="text-muted-foreground">Avg Acuity</p>
                        <p className="font-semibold">{nurse.average_acuity}</p>
                      </div>
                      <div>
                        <p className="text-muted-foreground">High Acuity</p>
                        <p className="font-semibold text-orange-600">{nurse.high_acuity_patients}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="high-acuity" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <AlertCircle className="h-5 w-5 text-orange-500" />
                High Acuity Patients (Level 4-5)
              </CardTitle>
              <CardDescription>
                {highAcuity?.high_acuity_count || 0} patients requiring intensive care
                ({highAcuity?.percentage || 0}% of census)
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {highAcuity?.patients?.slice(0, 20).map((patient, idx) => (
                  <div key={idx} className="p-3 border rounded-lg flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Heart className={`h-5 w-5 ${patient.acuity_level === 5 ? 'text-red-500' : 'text-orange-500'}`} />
                      <div>
                        <p className="font-medium">{patient.patient_name}</p>
                        <p className="text-xs text-muted-foreground">{patient.primary_diagnosis}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <Badge className={getAcuityColor(patient.acuity_level)}>
                        Level {patient.acuity_level}
                      </Badge>
                      <p className="text-xs text-muted-foreground mt-1">
                        Weight: {patient.caseload_weight}x
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="workload" className="mt-4">
          <div className="space-y-6">
            {/* Summary */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <Card>
                <CardContent className="p-4 text-center">
                  <p className="text-sm text-muted-foreground">Total Weighted Load</p>
                  <p className="text-2xl font-bold">{workloadAnalysis?.summary?.total_weighted_load || 0}</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4 text-center">
                  <p className="text-sm text-muted-foreground">Nurses Over Optimal</p>
                  <p className="text-2xl font-bold text-amber-600">{workloadAnalysis?.summary?.nurses_over_optimal || 0}</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4 text-center">
                  <p className="text-sm text-muted-foreground">Nurses with Capacity</p>
                  <p className="text-2xl font-bold text-emerald-600">{workloadAnalysis?.summary?.nurses_with_capacity || 0}</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4 text-center">
                  <p className="text-sm text-muted-foreground">Max Weighted Load</p>
                  <p className="text-2xl font-bold">{workloadAnalysis?.summary?.max_weighted_load || 0}</p>
                </CardContent>
              </Card>
            </div>

            {/* Recommendations */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Zap className="h-5 w-5 text-yellow-500" />
                  Workload Recommendations
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {workloadAnalysis?.recommendations?.map((rec, idx) => (
                    <div key={idx} className={`p-4 rounded-lg border-l-4 ${
                      rec.priority === 'high' ? 'border-l-red-500 bg-red-50' :
                      rec.priority === 'medium' ? 'border-l-amber-500 bg-amber-50' :
                      'border-l-blue-500 bg-blue-50'
                    }`}>
                      <div className="flex items-start gap-3">
                        <ChevronRight className="h-5 w-5 mt-0.5" />
                        <div>
                          <p className="font-medium">{rec.message}</p>
                          {rec.action && (
                            <p className="text-sm text-muted-foreground mt-1">{rec.action}</p>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Comparison Table */}
            <Card>
              <CardHeader>
                <CardTitle>Traditional vs Acuity-Weighted Caseloads</CardTitle>
                <CardDescription>See how acuity affects workload calculations</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left p-2">Nurse</th>
                        <th className="text-right p-2">Patients</th>
                        <th className="text-right p-2">Weighted Load</th>
                        <th className="text-right p-2">Difference</th>
                        <th className="text-right p-2">Avg Acuity</th>
                        <th className="text-center p-2">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {workloadAnalysis?.comparisons?.slice(0, 15).map((comp, idx) => (
                        <tr key={idx} className="border-b hover:bg-muted/30">
                          <td className="p-2">{comp.nurse_name}</td>
                          <td className="text-right p-2">{comp.traditional_patient_count}</td>
                          <td className="text-right p-2 font-medium">{comp.acuity_weighted_load}</td>
                          <td className={`text-right p-2 ${comp.difference > 0 ? 'text-amber-600' : 'text-emerald-600'}`}>
                            {comp.difference > 0 ? '+' : ''}{comp.difference}
                          </td>
                          <td className="text-right p-2">{comp.average_acuity}</td>
                          <td className="text-center p-2">
                            <Badge className={getWorkloadColor(comp.workload_status)} variant="outline">
                              {comp.workload_status?.replace('_', ' ')}
                            </Badge>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
